import CheckIcon from '@mui/icons-material/Check';
import { Box, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { ChoosePlanCards } from './components/ChoosePlanCards';

export const FeatureItem = ({ children }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <CheckIcon
        sx={{
          color: theme.palette.success.main,
          mr: 1.5,
          fontSize: '1.25rem',
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.primary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

export const ChooseYourPlan = () => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const { isLoggedIn, authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionCheckoutUrl = location.state?.subscriptionCheckoutUrl;
  const fromRegistration = location.state?.fromRegistration;
  const registrationData = location.state?.registrationData;

  // Check for pending registration data in sessionStorage
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null);

  useEffect(() => {
    // Check if we have pending registration data
    const storedData = sessionStorage.getItem('pendingRegistration');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setPendingRegistrationData(parsedData);
      } catch (error) {
        console.error('Error parsing pending registration data:', error);
      }
    }
  }, []);

  useEffect(() => {
    // If user is already logged in and we have pending registration, clear it
    if (isLoggedIn && pendingRegistrationData) {
      sessionStorage.removeItem('pendingRegistration');
      setPendingRegistrationData(null);
    }

    // If not logged in and no pending registration, redirect to home
    if (!isLoggedIn && !authLoading && !pendingRegistrationData && !fromRegistration) {
      navigate('/');
    }
  }, [isLoggedIn, authLoading, pendingRegistrationData, fromRegistration, navigate]);

  return (
    <Box
      sx={{
        flex: 1,
        background: darkMode ? theme.palette.background.default : '#F8F8F8',
        color: darkMode ? theme.palette.text.primary : '#111827',
        minHeight: '100vh',
        py: { xs: 5, md: 8 },
        transition: 'all 0.3s ease',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(30px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '@keyframes fadeInScale': {
          '0%': {
            opacity: 0,
            transform: 'scale(0.8)',
          },
          '100%': {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
        '@keyframes slideInLeft': {
          '0%': {
            opacity: 0,
            transform: 'translateX(-50px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes slideInRight': {
          '0%': {
            opacity: 0,
            transform: 'translateX(50px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes bounceIn': {
          '0%': {
            opacity: 0,
            transform: 'scale(0.3)',
          },
          '50%': {
            opacity: 1,
            transform: 'scale(1.05)',
          },
          '70%': {
            transform: 'scale(0.9)',
          },
          '100%': {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
      }}
    >
      <Box
        sx={{
          top: '2%',
          left: 15,
          position: 'fixed',
          display: { xs: 'none', md: 'block' },
          animation: 'slideInLeft 0.8s ease-out',
        }}
      >
        <img src="/icon/artwork.svg" alt="" />
      </Box>
      <Container maxWidth="xl">
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 5 },
            mt: { xs: 2, md: 0 },
            animation: 'fadeInUp 0.6s ease-out',
          }}
        >
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <img
              src="/icon/artwork.svg"
              alt="Oppy"
              style={{
                width: 120,
                height: 'auto',
                marginBottom: 8,
                objectFit: 'contain',
                animation: 'bounceIn 1s ease-out',
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: darkMode ? theme.palette.text.primary : '#111827',
                fontFamily: 'Power Grotesk',
                mb: 0.5,
                fontSize: { xs: 28, sm: 32, md: 36 },
                lineHeight: 1.2,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              {pendingRegistrationData ? 'Almost there!' : 'Welcome to Oppy!'}
              <br />
              Choose your plan
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: darkMode ? theme.palette.text.secondary : '#6B7280',
                fontWeight: 400,
                fontFamily: 'Space Grotesk',
                fontSize: { xs: 16, sm: 17, md: 18 },
                mt: 0.5,
                width: '80%',
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
              }}
            >
              {pendingRegistrationData
                ? 'Select your plan to complete your registration and start your free trial.'
                : 'Start with a 14-day free trial. No payment required today.'
              }
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography
              variant="h1"
              fontSize={{ xs: '28px', sm: '30px', md: '32px' }}
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: darkMode ? theme.palette.text.primary : '#111827',
                fontFamily: 'Power Grotesk',
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              {pendingRegistrationData ? 'Almost there! Choose your plan' : 'Welcome to Oppy! Choose your plan'}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: darkMode ? theme.palette.text.secondary : '#6B7280',
                fontWeight: 400,
                fontFamily: 'Space Grotesk',
                fontSize: { xs: '16px', sm: '17px', md: '18px' },
                animation: 'fadeInUp 0.8s ease-out 0.4s both',
              }}
            >
              {pendingRegistrationData
                ? 'Select your plan to complete your registration and start your free trial.'
                : 'Start with a 14-day free trial. No payment required today.'
              }
            </Typography>
          </Box>
        </Box>
        <ChoosePlanCards
          subscriptionCheckoutUrl={subscriptionCheckoutUrl}
          pendingRegistrationData={pendingRegistrationData || registrationData}
        />
      </Container>
    </Box>
  );
};

export default ChooseYourPlan;
